import { useRef, useEffect, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import { fmt, formatTime } from '../lib/formatters';

Chart.register(...registerables);

const MAX_POINTS = 200;
const THROTTLE_MS = 10000;

/**
 * Equity over time chart.
 * - Loads historical snapshots once from data attribute
 * - Appends live equity points throttled to 10s intervals
 * - Consistent time label format throughout
 * - Color changes based on equity vs balance
 */
export default function EquityChart({ equity, balance, initialHistory }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const historyRef = useRef([]);
  const lastAddedRef = useRef(0);
  const initializedRef = useRef(false);

  // Initialize chart once
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    Chart.defaults.color = 'rgba(255,255,255,0.6)';
    Chart.defaults.borderColor = 'rgba(255,255,255,0.06)';

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: [],
        datasets: [{
          label: 'Equity',
          data: [],
          borderColor: '#22c55e',
          backgroundColor: 'rgba(34,197,94,0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        scales: {
          x: {
            display: true,
            ticks: { maxTicksLimit: 8, font: { size: 10 } },
          },
          y: {
            beginAtZero: false,
            grid: { color: 'rgba(255,255,255,0.06)' },
            ticks: { callback: v => '$' + v.toFixed(0) },
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            callbacks: {
              title: (items) => items[0]?.label || '',
              label: (ctx) => `Equity: ${fmt(ctx.parsed.y)}`,
            },
          },
        },
      },
    });

    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, []);

  // Load historical data once
  useEffect(() => {
    if (initializedRef.current || !initialHistory?.length) return;
    initializedRef.current = true;

    historyRef.current = initialHistory.map(s => ({
      label: formatTime(new Date(s.recorded_at)),
      value: parseFloat(s.balance_usd),
    }));

    // Trim to max
    if (historyRef.current.length > MAX_POINTS) {
      historyRef.current = historyRef.current.slice(-MAX_POINTS);
    }

    updateChart(balance);
  }, [initialHistory]);

  const updateChart = useCallback((currentBalance) => {
    if (!chartRef.current?.canvas?.parentNode) return;
    const ds = chartRef.current.data.datasets[0];
    const values = historyRef.current.map(h => h.value);
    chartRef.current.data.labels = historyRef.current.map(h => h.label);
    ds.data = values;
    const latest = values[values.length - 1] || 0;
    ds.borderColor = latest >= (currentBalance || 1000) ? '#22c55e' : '#ef4444';
    ds.backgroundColor = latest >= (currentBalance || 1000) ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)';

    // Set Y-axis range based on data to avoid starting from $0
    if (values.length > 0) {
      const min = Math.min(...values);
      const max = Math.max(...values);
      const padding = Math.max((max - min) * 0.1, 5);
      chartRef.current.options.scales.y.suggestedMin = Math.floor(min - padding);
      chartRef.current.options.scales.y.suggestedMax = Math.ceil(max + padding);
    }

    chartRef.current.update('none');
  }, []);

  // Append live equity points (throttled, skip zero/invalid)
  useEffect(() => {
    if (!equity || equity <= 0 || !chartRef.current) return;

    const now = Date.now();
    if (now - lastAddedRef.current < THROTTLE_MS) return;
    lastAddedRef.current = now;

    historyRef.current.push({
      label: formatTime(new Date()),
      value: equity,
    });

    if (historyRef.current.length > MAX_POINTS) {
      historyRef.current.shift();
    }

    updateChart(balance);
  }, [equity, balance, updateChart]);

  return (
    <div className="card bg-base-200 shadow-xl lg:col-span-2">
      <div className="card-body">
        <h2 className="card-title text-sm opacity-70">Equity Over Time</h2>
        <div className="h-[260px]">
          <canvas ref={canvasRef}></canvas>
        </div>
      </div>
    </div>
  );
}
